import { useEffect, useEffectEvent, useMemo } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import { DEFAULT_PAGE_LAYOUT_GAP, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN_HEIGHT_STICKY,
  STICKY_RIGHT_DEFAULTS,
  type WrappingLayoutColumnDef,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ResponsiveBoxSizing, SpaceUnit } from '@interxfi/design-system/util/responsive';
import {
  getInitialFormValuesForProgramBrandingSpec,
  ProgramBrandingForm,
  type ProgramBrandingFormValues,
  programBrandingFormSchema,
} from '../../../../../../../../../features/userauth/program/branding/form/program-branding-form';
import { getProgramBrandingConfigurationPath } from '../../../../../../../../../features/userauth/program/program-routing';

export const Route = createFileRoute('/_app/_platform/_context-required/program/_program/$programId/_program/branding/')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `program-${params.programId}-branding-configuration`,
        label: 'Document Designer',
        to: getProgramBrandingConfigurationPath(params.programId),
      }),
    }) satisfies AggregateBreadcrumb,
  component: ProgramBrandingConfiguration,
});

const padding = {
  bottom: { sm: '16px', md: '32px', lg: '32px', xl: '32px', xxl: '32px' },
  left: { sm: '16px', md: '32px', lg: '32px', xl: '32px', xxl: '32px' },
  right: { sm: '16px', md: '32px', lg: '32px', xl: '32px', xxl: '32px' },
  top: { sm: '16px', md: '32px', lg: '32px', xl: '32px', xxl: '32px' },
} as const satisfies ResponsiveBoxSizing<SpaceUnit>;

const leftColumnContainer = {
  width: { type: 'min', min: 500 },
  height: DEFAULT_WRAPPING_LAYOUT_COLUMN_HEIGHT_STICKY,
  overflow: 'auto',
  ...STICKY_RIGHT_DEFAULTS,
} as const satisfies Omit<WrappingLayoutColumnDef, 'render'>;

const rightColumnContainer = {
  width: { type: 'fixed', width: 420 },
  height: DEFAULT_WRAPPING_LAYOUT_COLUMN_HEIGHT_STICKY,
  overflow: 'auto',
  ...STICKY_RIGHT_DEFAULTS,
} as const satisfies Omit<WrappingLayoutColumnDef, 'render'>;

function ProgramBrandingConfiguration() {
  const { programId } = Route.useParams();
  const { data } = useGetProgram({ programId });
  const defaultValues = useMemo(
    () => getInitialFormValuesForProgramBrandingSpec(data?.program?.data?.currentBrandingVersion?.branding),
    [data?.program?.data?.currentBrandingVersion?.branding],
  );
  const form = useForm({ defaultValues, mode: 'all', resolver: zodResolver(programBrandingFormSchema) });
  const { control, handleSubmit, reset: resetForm } = form;
  const lens = useLens({ control });

  const resetToDefaultValues = useEffectEvent((values: ProgramBrandingFormValues) => {
    resetForm(values);
  });

  useEffect(() => {
    resetToDefaultValues(defaultValues);
  }, [defaultValues]);

  return (
    <FormProvider {...form}>
      <PageLayout gap={7} padding={padding}>
        <PageLayout.Section noBorder>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...leftColumnContainer,
              render: null,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...rightColumnContainer,
              render: (
                <form onSubmit={handleSubmit(console.log)}>
                  <ProgramBrandingForm lens={lens} programId={programId} />
                </form>
              ),
            }}
            wrap="forward"
          />
        </PageLayout.Section>
      </PageLayout>
    </FormProvider>
  );
}
